import { useState, useEffect, useCallback } from "react";
import {
  getDocs,
  query,
  where,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  OrgEmailTemplatesRef,
  GenEmailTemplatesRef,
  storage,
  AdminsRef,
} from "../../../firebase";

// Template scopes:
//  - "org": orgEmailTemplates (scoped by `organization`)
//  - "gen": genEmailTemplates (standard templates, no `organization` field)
export const EMAIL_TEMPLATE_SCOPE_ORG = "org";
export const EMAIL_TEMPLATE_SCOPE_GEN = "gen";

const getTemplatesRef = (scope) =>
  scope === EMAIL_TEMPLATE_SCOPE_GEN ? GenEmailTemplatesRef : OrgEmailTemplatesRef;

// Ensure only one default template per organization
const ensureSingleDefault = async (organizationId, templateId) => {
  if (!organizationId || !templateId) return;

  const q = query(
    OrgEmailTemplatesRef,
    where("organization", "==", organizationId),
    where("default", "==", true)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return;

  const batch = writeBatch(OrgEmailTemplatesRef.firestore);

  snapshot.forEach((docSnap) => {
    if (docSnap.id !== templateId) {
      batch.update(doc(OrgEmailTemplatesRef, docSnap.id), { default: false });
    }
  });

  await batch.commit();
};

// Hook to fetch all email templates for an organization (scope "org"),
// or all standard templates (scope "gen"). `activeOnly` limits to status active.
export const useGetEmailTemplates = (
  organizationId,
  { scope = EMAIL_TEMPLATE_SCOPE_ORG, activeOnly = false } = {}
) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async () => {
    const isGen = scope === EMAIL_TEMPLATE_SCOPE_GEN;
    if (!isGen && !organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const constraints = [];
      if (!isGen) constraints.push(where("organization", "==", organizationId));
      if (activeOnly) constraints.push(where("status", "==", "active"));
      const q = query(getTemplatesRef(scope), ...constraints);
      const querySnapshot = await getDocs(q);
      
      const templatesData = [];
      for (const docSnapshot of querySnapshot.docs) {
        const templateData = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Fetch updatedBy user data if exists
        if (templateData.updatedBy) {
          try {
            const userDoc = await getDoc(doc(AdminsRef, templateData.updatedBy));
            if (userDoc.exists()) {
              templateData.updatedByData = userDoc.data();
            }
          } catch (e) {
            console.log("Could not fetch updatedBy user:", e);
          }
        }
        
        // Fetch createdBy user data if exists
        if (templateData.createdBy) {
          try {
            const userDoc = await getDoc(doc(AdminsRef, templateData.createdBy));
            if (userDoc.exists()) {
              templateData.createdByData = userDoc.data();
            }
          } catch (e) {
            console.log("Could not fetch createdBy user:", e);
          }
        }
        
        templatesData.push(templateData);
      }
      
      setTemplates(templatesData);
      setError(null);
    } catch (err) {
      console.error("Error fetching email templates:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, scope, activeOnly]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, loading, error, refetch: fetchTemplates };
};

// Hook to fetch a single email template by ID
export const useGetEmailTemplateById = (
  templateId,
  scope = EMAIL_TEMPLATE_SCOPE_ORG
) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplate = useCallback(async () => {
    if (!templateId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const templateDoc = await getDoc(doc(getTemplatesRef(scope), templateId));
      
      if (templateDoc.exists()) {
        const templateData = { id: templateDoc.id, ...templateDoc.data() };
        
        // Fetch updatedBy user data
        if (templateData.updatedBy) {
          try {
            const userDoc = await getDoc(doc(AdminsRef, templateData.updatedBy));
            if (userDoc.exists()) {
              templateData.updatedByData = userDoc.data();
            }
          } catch (e) {
            console.log("Could not fetch updatedBy user:", e);
          }
        }
        
        // Fetch createdBy user data
        if (templateData.createdBy) {
          try {
            const userDoc = await getDoc(doc(AdminsRef, templateData.createdBy));
            if (userDoc.exists()) {
              templateData.createdByData = userDoc.data();
            }
          } catch (e) {
            console.log("Could not fetch createdBy user:", e);
          }
        }
        
        setTemplate(templateData);
        setError(null);
      } else {
        setError("Template not found");
      }
    } catch (err) {
      console.error("Error fetching email template:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, scope]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  return { template, loading, error, refetch: fetchTemplate };
};

// Hook to create a new email template
export const useCreateEmailTemplate = (scope = EMAIL_TEMPLATE_SCOPE_ORG) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createTemplate = async (templateData, userId, organizationId) => {
    try {
      setLoading(true);
      setError(null);
      const isGen = scope === EMAIL_TEMPLATE_SCOPE_GEN;

      const newTemplate = {
        ...(isGen ? {} : { organization: organizationId }),
        name: templateData.name || "",
        type: "Narrator Update", // Hardcoded as per requirements
        description: templateData.description || "",
        subject: templateData.subject || "",
        status: templateData.status || "active",
        logo: templateData.logo || "",
        topSection: templateData.topSection || "",
        bottomSection: templateData.bottomSection || "",
        // Narrator Page Details
        narratorCanUpdateAudio:
          typeof templateData.narratorCanUpdateAudio === "boolean"
            ? templateData.narratorCanUpdateAudio
            : true,
        narratorCanUpdatePhoto:
          typeof templateData.narratorCanUpdatePhoto === "boolean"
            ? templateData.narratorCanUpdatePhoto
            : false,
        narratorCanUpdateTitle:
          typeof templateData.narratorCanUpdateTitle === "boolean"
            ? templateData.narratorCanUpdateTitle
            : false,
        narratorCanUpdateDescription:
          typeof templateData.narratorCanUpdateDescription === "boolean"
            ? templateData.narratorCanUpdateDescription
            : false,
        narratorGreeting: templateData.narratorGreeting || "",
        // Standard templates never carry a default flag
        default: isGen ? false : !!templateData.default,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedBy: userId,
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(getTemplatesRef(scope), newTemplate);

      // If this template is marked as default, unset default on other templates in the org
      if (!isGen && newTemplate.default) {
        try {
          await ensureSingleDefault(organizationId, docRef.id);
        } catch (e) {
          console.error(
            "Error ensuring single default template after create:",
            e
          );
        }
      }

      return { success: true, id: docRef.id };
    } catch (err) {
      console.error("Error creating email template:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { createTemplate, loading, error };
};

// Hook to update an existing email template
export const useUpdateEmailTemplate = (scope = EMAIL_TEMPLATE_SCOPE_ORG) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateTemplate = async (
    templateId,
    templateData,
    userId,
    organizationId
  ) => {
    try {
      setLoading(true);
      setError(null);

      const updateData = {
        ...templateData,
        type: "Narrator Update", // Always hardcode type
        updatedBy: userId,
        updatedAt: serverTimestamp(),
      };

      // Remove fields that shouldn't be updated
      delete updateData.id;
      delete updateData.createdBy;
      delete updateData.createdAt;
      delete updateData.createdByData;
      delete updateData.updatedByData;

      const isGen = scope === EMAIL_TEMPLATE_SCOPE_GEN;
      if (isGen) {
        updateData.default = false;
        delete updateData.organization;
      }

      await updateDoc(doc(getTemplatesRef(scope), templateId), updateData);

      // If this template is marked as default, unset default on other templates in the org
      if (!isGen && templateData.default && organizationId) {
        try {
          await ensureSingleDefault(organizationId, templateId);
        } catch (e) {
          console.error(
            "Error ensuring single default template after update:",
            e
          );
        }
      }

      return { success: true };
    } catch (err) {
      console.error("Error updating email template:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { updateTemplate, loading, error };
};

// Hook to update status of multiple templates (bulk action)
export const useBulkUpdateTemplateStatus = (scope = EMAIL_TEMPLATE_SCOPE_ORG) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const bulkUpdateStatus = async (templateIds, newStatus, userId) => {
    try {
      setLoading(true);
      setError(null);

      const updatePromises = templateIds.map((templateId) =>
        updateDoc(doc(getTemplatesRef(scope), templateId), {
          status: newStatus,
          updatedBy: userId,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);
      return { success: true };
    } catch (err) {
      console.error("Error bulk updating template status:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { bulkUpdateStatus, loading, error };
};

// Hook to duplicate templates (bulk action)
export const useDuplicateTemplates = (scope = EMAIL_TEMPLATE_SCOPE_ORG) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const duplicateTemplates = async (templateIds, userId, organizationId) => {
    try {
      setLoading(true);
      setError(null);
      const templatesRef = getTemplatesRef(scope);

      const duplicatePromises = templateIds.map(async (templateId) => {
        // Get the original template
        const templateDoc = await getDoc(doc(templatesRef, templateId));
        if (templateDoc.exists()) {
          const originalData = templateDoc.data();
          
          // Create duplicate with modified name
          const duplicateData = {
            ...originalData,
            name: `${originalData.name} (Copy)`,
            createdBy: userId,
            createdAt: serverTimestamp(),
            updatedBy: userId,
            updatedAt: serverTimestamp(),
            default: false,
          };
          
          return addDoc(templatesRef, duplicateData);
        }
      });

      await Promise.all(duplicatePromises);
      return { success: true };
    } catch (err) {
      console.error("Error duplicating templates:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { duplicateTemplates, loading, error };
};

// Hook to upload logo image
export const useUploadTemplateLogo = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const uploadLogo = async (file, organizationId) => {
    try {
      setLoading(true);
      setError(null);

      // Standard templates (no organizationId) are stored under a shared folder
      const folder = organizationId || "standard";
      const fileName = `email_templates/${folder}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return { success: true, url: downloadURL };
    } catch (err) {
      console.error("Error uploading logo:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { uploadLogo, loading, error };
};

// Hook to copy standard templates (genEmailTemplates) into an organization's
// templates (orgEmailTemplates). Copies are independent snapshots: later edits
// on either side do not affect the other.
//  - `organization` is set to the org ID
//  - `default` is always false
//  - logo is replaced with the org logo (orgLogo) when one is provided
//  - if the org already has a template with the same name, " - copy" is
//    appended (repeated until the name is unique)
export const useAddStandardTemplatesToOrg = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const addStandardTemplatesToOrg = async (
    templateIds,
    organizationId,
    userId,
    orgLogo
  ) => {
    try {
      setLoading(true);
      setError(null);

      if (!organizationId) throw new Error("Missing organization ID");

      // Existing org template names (case-insensitive) for collision checks
      const orgSnapshot = await getDocs(
        query(OrgEmailTemplatesRef, where("organization", "==", organizationId))
      );
      const existingNames = new Set(
        orgSnapshot.docs.map((d) =>
          String(d.data().name || "").trim().toLowerCase()
        )
      );

      const sourceDocs = await Promise.all(
        templateIds.map((id) => getDoc(doc(GenEmailTemplatesRef, id)))
      );

      const batch = writeBatch(OrgEmailTemplatesRef.firestore);
      let count = 0;

      sourceDocs.forEach((sourceDoc) => {
        if (!sourceDoc.exists()) return;
        const sourceData = sourceDoc.data();

        let name = String(sourceData.name || "").trim();
        while (existingNames.has(name.toLowerCase())) {
          name = `${name} - copy`;
        }
        existingNames.add(name.toLowerCase());

        const newDocRef = doc(OrgEmailTemplatesRef);
        batch.set(newDocRef, {
          ...sourceData,
          name,
          organization: organizationId,
          logo: orgLogo || sourceData.logo || "",
          default: false,
          sourceGenTemplateId: sourceDoc.id,
          createdBy: userId,
          createdAt: serverTimestamp(),
          updatedBy: userId,
          updatedAt: serverTimestamp(),
        });
        count += 1;
      });

      if (count > 0) await batch.commit();
      return { success: true, count };
    } catch (err) {
      console.error("Error adding standard templates to organization:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { addStandardTemplatesToOrg, loading, error };
};

